import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { fmtCurrencyFull, fmtPct } from "@/lib/format";
import { fetchActiveSponsors } from "@/lib/api";

const COLORS = {
  primary: "#2B5339",
  text1: "#1C2B23",
  text2: "#5C6B62",
  accent: "#C26747",
  border: "#E5E2DC",
};

const captureChart = async (chartSelector) => {
  const el = document.querySelector(chartSelector);
  if (!el) return null;
  try {
    const canvas = await html2canvas(el, {
      backgroundColor: "#ffffff",
      scale: 2,
      logging: false,
      useCORS: true,
    });
    return canvas.toDataURL("image/png");
  } catch (e) {
    console.warn("chart capture failed", e);
    return null;
  }
};

export const exportPlanPDF = async ({ inputs, result, chartSelector }) => {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 48;
  let y = margin;

  // Header
  doc.setFillColor(43, 83, 57);
  doc.rect(0, 0, pageW, 64, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("Centennial", margin, 40);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text("Your 100-year retirement plan", pageW - margin, 40, { align: "right" });
  y = 100;

  // Plan summary
  doc.setTextColor(28, 43, 35);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Plan summary", margin, y);
  y += 22;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(92, 107, 98);

  const summaryRows = [
    ["Current age", `${inputs.current_age} yrs`],
    ["Retirement age", `${inputs.retirement_age} yrs`],
    ["Life expectancy", `${inputs.life_expectancy} yrs`],
    ["Current savings", fmtCurrencyFull(inputs.current_savings)],
    ["Monthly contribution", fmtCurrencyFull(inputs.monthly_contribution)],
    ["Annual withdrawal (today's $)", fmtCurrencyFull(inputs.annual_withdrawal)],
    ["Inflation rate", `${inputs.inflation_rate}%`],
    ["Allocation", `Stocks ${inputs.allocation.stocks}% / Bonds ${inputs.allocation.bonds}% / Cash ${inputs.allocation.cash}%`],
    ["Withdrawal strategy", (inputs.withdrawal_strategy || "fixed_real").replace("_", " ")],
  ];
  if (inputs.income_streams?.length) {
    summaryRows.push([
      "Income streams",
      inputs.income_streams.map((s) => `${s.name}: ${fmtCurrencyFull(s.annual_amount)} @ ${s.start_age}`).join("; "),
    ]);
  }

  summaryRows.forEach(([k, v]) => {
    doc.setTextColor(92, 107, 98);
    doc.text(k, margin, y);
    doc.setTextColor(28, 43, 35);
    doc.text(String(v), margin + 200, y);
    y += 16;
  });

  // Results highlight box
  y += 12;
  doc.setFillColor(240, 239, 234);
  doc.roundedRect(margin, y, pageW - margin * 2, 90, 8, 8, "F");
  const innerX = margin + 18;
  const colW = (pageW - margin * 2 - 36) / 3;

  const items = [
    ["Success probability", fmtPct(result.summary.success_probability_pct, 1)],
    ["Median final balance", fmtCurrencyFull(result.summary.median_final_balance)],
    ["P10 — P90 range", `${fmtCurrencyFull(result.summary.p10_final_balance)} — ${fmtCurrencyFull(result.summary.p90_final_balance)}`],
  ];
  items.forEach(([label, val], i) => {
    const x = innerX + i * colW;
    doc.setTextColor(92, 107, 98);
    doc.setFontSize(8);
    doc.text(label.toUpperCase(), x, y + 24);
    doc.setTextColor(28, 43, 35);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(val, x, y + 48);
    doc.setFont("helvetica", "normal");
  });
  y += 110;

  // Chart image
  const chartImg = chartSelector ? await captureChart(chartSelector) : null;
  if (chartImg) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(28, 43, 35);
    doc.text("Projection over time", margin, y);
    y += 12;
    const imgW = pageW - margin * 2;
    const imgH = imgW * 0.5;
    doc.addImage(chartImg, "PNG", margin, y, imgW, imgH, undefined, "FAST");
    y += imgH + 16;
  }

  // Disclaimer
  if (y > 720) {
    doc.addPage();
    y = margin;
  }
  doc.setFontSize(8);
  doc.setTextColor(140, 145, 140);
  const disclaimer =
    "Not financial advice. Projections use live market data blended with long-run historical priors. Past returns are not a guarantee. Generated by Centennial — a free, no-signup retirement calculator.";
  const split = doc.splitTextToSize(disclaimer, pageW - margin * 2);
  doc.text(split, margin, y);
  y += split.length * 10 + 12;

  // Sponsor footer (only prints if there's at least one active sponsor)
  try {
    const { sponsors } = await fetchActiveSponsors();
    if (sponsors && sponsors.length > 0) {
      const names = sponsors.map((s) => s.display_name).join(" · ");
      doc.setFontSize(8);
      doc.setTextColor(43, 83, 57);
      doc.text(`This report brought to you by ${names}`, margin, y);
    }
  } catch {
    /* sponsor footer is optional; never block the PDF */
  }

  doc.save(`centennial-plan-${new Date().toISOString().slice(0, 10)}.pdf`);
};
